import React from 'react';
import { motion } from 'framer-motion';
import { gameUIVariants } from '../../lib/animations';

interface CosmicButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  icon?: React.ReactNode;
}

const CosmicButton: React.FC<CosmicButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  className = '',
  type = 'button',
  icon
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return 'border-stellar-blue text-stellar-blue hover:bg-stellar-blue hover:text-cosmic-black';
      case 'secondary':
        return 'border-stellar-cyan text-stellar-cyan hover:bg-stellar-cyan hover:text-cosmic-black';
      case 'danger':
        return 'border-danger-red text-danger-red hover:bg-danger-red hover:text-white';
      case 'success':
        return 'border-success-emerald text-success-emerald hover:bg-success-emerald hover:text-cosmic-black';
      case 'warning':
        return 'border-warning-amber text-warning-amber hover:bg-warning-amber hover:text-cosmic-black';
      default:
        return 'border-stellar-blue text-stellar-blue hover:bg-stellar-blue hover:text-cosmic-black';
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return 'px-4 py-2 text-sm';
      case 'md':
        return 'px-6 py-3 text-base';
      case 'lg':
        return 'px-8 py-4 text-lg';
      default:
        return 'px-6 py-3 text-base';
    }
  };

  const getGlowColor = () => {
    switch (variant) {
      case 'primary':
        return '0 0 20px var(--stellar-blue)';
      case 'secondary':
        return '0 0 20px var(--stellar-cyan)';
      case 'danger':
        return '0 0 20px var(--danger-red)';
      case 'success':
        return '0 0 20px var(--success-emerald)';
      case 'warning':
        return '0 0 20px var(--warning-amber)';
      default:
        return '0 0 20px var(--stellar-blue)';
    }
  };

  return (
    <motion.button
      type={type}
      className={`
        relative bg-transparent border-2 font-display font-medium uppercase tracking-wider
        transition-all duration-300 group overflow-hidden
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-cosmic-black
        disabled:opacity-50 disabled:cursor-not-allowed
        ${getVariantStyles()}
        ${getSizeStyles()}
        ${className}
      `}
      variants={gameUIVariants}
      whileHover={disabled ? undefined : "buttonHover"}
      whileTap={disabled ? undefined : "buttonTap"}
      onClick={disabled || loading ? undefined : onClick}
      disabled={disabled || loading}
      style={{
        boxShadow: disabled ? '0 0 0px rgba(0, 0, 0, 0)' : '0 0 0px rgba(0, 212, 255, 0)'
      }}
    >
      {/* Animated background sweep */}
      <motion.div
        className="absolute inset-0 bg-current opacity-0 group-hover:opacity-20"
        initial={{ x: '-100%' }}
        whileHover={{ x: '100%' }}
        transition={{ duration: 0.5 }}
      />

      {/* Loading spinner */}
      {loading && (
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <motion.div
            className="w-5 h-5 border-2 border-current border-t-transparent rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
        </motion.div>
      )}

      {/* Content */}
      <div className={`relative z-10 flex items-center justify-center gap-2 ${loading ? 'opacity-0' : ''}`}>
        {icon && <span className="text-lg">{icon}</span>}
        {children}
      </div>

      {/* Glow effect on hover */}
      {!disabled && (
        <motion.div
          className="absolute inset-0 rounded border-current"
          initial={{ 
            opacity: 0,
            boxShadow: '0 0 0px rgba(0, 0, 0, 0)'
          }}
          whileHover={{ 
            opacity: 0.3,
            boxShadow: getGlowColor()
          }}
          transition={{ duration: 0.2 }}
        />
      )}
    </motion.button>
  );
};

// Specialized button variants
export const PrimaryButton: React.FC<Omit<CosmicButtonProps, 'variant'>> = (props) => (
  <CosmicButton variant="primary" {...props} />
);

export const SecondaryButton: React.FC<Omit<CosmicButtonProps, 'variant'>> = (props) => (
  <CosmicButton variant="secondary" {...props} />
);

export const DangerButton: React.FC<Omit<CosmicButtonProps, 'variant'>> = (props) => (
  <CosmicButton variant="danger" {...props} />
);

export const SuccessButton: React.FC<Omit<CosmicButtonProps, 'variant'>> = (props) => (
  <CosmicButton variant="success" {...props} />
);

export const WarningButton: React.FC<Omit<CosmicButtonProps, 'variant'>> = (props) => (
  <CosmicButton variant="warning" {...props} />
);

export default CosmicButton; 